export interface DataBinding {
    customApiId: string;
    apiKey: string;
    fallbackValue?: string;
}

export function resolveJsonPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    if (path === '.' || path === '') return obj;

    const cleanPath = path.replace(/\[(\d+)\]/g, '.$1');
    const parts = cleanPath.split('.').filter(Boolean);
    let current = obj;
    for (const part of parts) {
        if (current === null || current === undefined) return undefined;
        current = current[part];
    }
    return current;
}

export function resolveTemplateBindings(
    textContents: Record<string, string> = {},
    textStyles: Record<string, any> = {},
    imageSettings: Record<string, any> = {},
    scrollerSettings: Record<string, any> = {},
    customApis: any[] = [],
    showPlaceholders: boolean = false
) {
    const resolvedTextContents = { ...textContents };
    const resolvedImageSettings = { ...imageSettings };
    const resolvedScrollerSettings = { ...scrollerSettings };

    // Helper to log or handle replacement
    const getResolvedValue = (binding: DataBinding, isText: boolean = false): string | null => {
        if (!binding.customApiId || !binding.apiKey) {
            return null;
        }
        if (showPlaceholders) {
            const api = customApis.find((a) => String(a._id) === String(binding.customApiId));
            const apiLabel = api ? api.name_custom : 'API';
            const apiKeyPlaceholder = binding.apiKey
                .replace(/\[\d+\]/g, '[n]')
                .replace(/\.\d+\./g, '.n.')
                .replace(/\.\d+$/g, '.n')
                .replace(/^\d+\./g, 'n.');
            if (isText) {
                return `{{${apiLabel}.${apiKeyPlaceholder}}}`;
            } else {
                const textToDisplay = `${apiLabel}.${apiKeyPlaceholder}`;
                return `https://placehold.co/600x400/101928/FFFFFF?text=${encodeURIComponent(textToDisplay)}`;
            }
        }
        const api = customApis.find((a) => String(a._id) === String(binding.customApiId));
        if (api && api.customapidata) {
            const val = resolveJsonPath(api.customapidata, binding.apiKey);
            if (val !== undefined && val !== null && String(val).trim() !== '' && String(val) !== 'null' && String(val) !== 'undefined') {
                return String(val);
            }
        }
        if (binding.fallbackValue !== undefined && String(binding.fallbackValue).trim() !== '' && String(binding.fallbackValue) !== 'null') {
            return binding.fallbackValue;
        }
        if (isText) {
            const cleanKey = binding.apiKey.split('.').pop() || 'Data';
            const keyPlaceholder = `{{${cleanKey}}}`;
            return keyPlaceholder;
        }
        return null;
    };

    // 1. Resolve Text Contents
    Object.keys(textStyles).forEach((layerId) => {
        const style = textStyles[layerId];
        if (style?.binding) {
            const resolved = getResolvedValue(style.binding, true);
            if (resolved !== null) {
                resolvedTextContents[layerId] = resolved;
            }
        }
    });

    // 2. Resolve Image Urls
    Object.keys(imageSettings).forEach((layerId) => {
        const setting = imageSettings[layerId];
        if (setting?.binding) {
            const resolved = getResolvedValue(setting.binding);
            if (resolved !== null) {
                resolvedImageSettings[layerId] = {
                    ...setting,
                    url: resolved,
                };
            }
        }
    });

    // 3. Resolve Scroller Contents
    Object.keys(scrollerSettings).forEach((layerId) => {
        const setting = scrollerSettings[layerId];
        if (setting?.binding) {
            const resolved = getResolvedValue(setting.binding, true);
            if (resolved !== null) {
                resolvedScrollerSettings[layerId] = {
                    ...setting,
                    content: resolved,
                };
            }
        }
    });

    return {
        textContents: resolvedTextContents,
        imageSettings: resolvedImageSettings,
        scrollerSettings: resolvedScrollerSettings,
    };
}

export function findFirstArrayPath(obj: any, path: string = ''): string | null {
    if (!obj) return null;
    if (Array.isArray(obj)) return path;
    if (typeof obj === 'object') {
        const technicalKeys = new Set([
            'headers', 'config', 'request', 'response', 'status', 'statuscode',
            'success', 'message', 'error', 'errors', 'ok', 'method', 'url', 'origin',
            'headers_list', 'header_values', 'request_body', 'api_url', 'token',
            'total', 'skip', 'limit', 'count', 'size', 'page', 'pages', 'totalpages', 'perpage',
            'data_json'
        ]);
        for (const key of Object.keys(obj)) {
            if (technicalKeys.has(key.toLowerCase())) continue;
            const val = obj[key];
            const childPath = path ? `${path}.${key}` : key;
            if (Array.isArray(val)) {
                return childPath;
            }
            if (val && typeof val === 'object') {
                const nested = findFirstArrayPath(val, childPath);
                if (nested !== null) return nested;
            }
        }
    }
    return null;
}


